<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";

import {
  createLocation,
  createSiteDiary,
  getProject,
  listLocations,
  listSiteDiaries,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const router = useRouter();
const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());

const { data, isPending, isError, error } = useQuery({
  queryKey: ["site-diaries", selectedProjectId],
  queryFn: () => listSiteDiaries(selectedProjectId.value),
});

// Current project context — pre-fills the diary header.
const { data: project } = useQuery({
  queryKey: ["project", selectedProjectId],
  queryFn: () =>
    selectedProjectId.value
      ? getProject(selectedProjectId.value)
      : Promise.reject(new Error("No project")),
  enabled: () => !!selectedProjectId.value,
});

// The project's registered sites — drive the location picker.
const { data: locations } = useQuery({
  queryKey: ["locations", selectedProjectId],
  queryFn: () => listLocations(selectedProjectId.value),
  enabled: () => !!selectedProjectId.value,
});

const open = (id: string): void => {
  void router.push({
    name: "site-diary-detail",
    params: { projectId: selectedProjectId.value, id },
  });
};

// ISO-8601 week number — matches how the office counts "this week".
function isoWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7) + 3);
  const firstThu = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  firstThu.setUTCDate(firstThu.getUTCDate() - ((firstThu.getUTCDay() + 6) % 7) + 3);
  return 1 + Math.round((t.getTime() - firstThu.getTime()) / (7 * 86_400_000));
}

// --- create (office pre-population, brief §1.2) ---
const today = new Date().toISOString().slice(0, 10);
const form = reactive({
  diary_date: today,
  shift_start: "",
  shift_end: "",
  rail_week: "",
  client_name: "",
  contract_po_number: "",
});
const touched = reactive({ rail_week: false, client_name: false });
const creating = ref(false);
const createError = ref<string | null>(null);
const projectName = computed(() => project.value?.name ?? "");

// Auto-populate rail week from the date, and client from the project —
// each stays editable (a manual edit stops the auto-fill overwriting it).
watch(
  () => form.diary_date,
  (d) => {
    if (!touched.rail_week && d) form.rail_week = String(isoWeek(new Date(d)));
  },
  { immediate: true },
);
watch(
  project,
  (p) => {
    if (p && !touched.client_name && !form.client_name) {
      form.client_name = p.client_name ?? "";
    }
  },
  { immediate: true },
);

// --- location picker (pick a registered site, add one, or type freehand) ---
const MANUAL = "__manual__";
const locationChoice = ref("");
const manualLocation = ref("");
const siteLabel = (loc: { ref: string; name: string | null }): string =>
  loc.name ? `${loc.ref} — ${loc.name}` : loc.ref;
const resolvedSite = computed(() =>
  locationChoice.value === MANUAL ? manualLocation.value.trim() : locationChoice.value,
);

const showNewSite = ref(false);
const newSite = reactive({ ref: "", name: "", structure_type: "" });
const addingSite = ref(false);

async function addSite(): Promise<void> {
  if (!selectedProjectId.value || !newSite.ref.trim()) return;
  addingSite.value = true;
  try {
    const loc = await createLocation({
      project_id: selectedProjectId.value,
      ref: newSite.ref.trim(),
      name: newSite.name.trim() || undefined,
      structure_type: newSite.structure_type.trim() || undefined,
    });
    await queryClient.invalidateQueries({ queryKey: ["locations"] });
    locationChoice.value = siteLabel(loc);
    showNewSite.value = false;
    newSite.ref = newSite.name = newSite.structure_type = "";
  } finally {
    addingSite.value = false;
  }
}

async function create(): Promise<void> {
  if (!selectedProjectId.value || !projectName.value) {
    createError.value = "No project selected.";
    return;
  }
  if (!form.diary_date) {
    createError.value = "A date is required.";
    return;
  }
  creating.value = true;
  createError.value = null;
  try {
    const diaryId = await createSiteDiary({
      project_id: selectedProjectId.value,
      project_name: projectName.value,
      project_code: project.value?.code ?? undefined,
      diary_date: form.diary_date,
      shift_start: form.shift_start,
      shift_end: form.shift_end,
      site_description: resolvedSite.value,
      rail_week: form.rail_week,
      client_name: form.client_name,
      contract_po_number: form.contract_po_number,
    });
    await queryClient.invalidateQueries({ queryKey: ["site-diaries"] });
    open(diaryId);
  } catch (e) {
    createError.value = e instanceof Error ? e.message : String(e);
  } finally {
    creating.value = false;
  }
}
</script>

<template>
  <PageLayout>
    <h1 class="text-xl font-semibold mb-1">
      Site diaries
    </h1>
    <p class="mb-4 text-sm text-neutral-500">
      Create and pre-populate a diary here before the shift — the site team
      completes it on the mobile app.
    </p>

    <!-- New site diary (office pre-population) -->
    <section class="mb-6 rounded-xl border border-neutral-200 bg-neutral-0 p-6">
      <h2 class="mb-1 font-semibold">
        New site diary
      </h2>
      <p class="mb-4 text-sm text-neutral-500">
        Filed against
        <span class="font-medium text-neutral-700">{{ projectName || "this project" }}</span><span
          v-if="project?.code"
          class="text-neutral-400"
        > · {{ project.code }}</span>.
      </p>
      <div class="grid max-w-2xl gap-x-4 gap-y-3 sm:grid-cols-2">
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Date</span>
          <input
            v-model="form.diary_date"
            type="date"
            class="rounded-md border border-neutral-300 px-3 py-2"
          >
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Rail week <span class="text-neutral-400">(optional)</span></span>
          <input
            v-model="form.rail_week"
            type="text"
            placeholder="e.g. 24"
            class="rounded-md border border-neutral-300 px-3 py-2"
            @input="touched.rail_week = true"
          >
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Shift start <span class="text-neutral-400">(optional)</span></span>
          <input
            v-model="form.shift_start"
            type="text"
            placeholder="22:00"
            class="rounded-md border border-neutral-300 px-3 py-2"
          >
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Shift finish <span class="text-neutral-400">(optional)</span></span>
          <input
            v-model="form.shift_end"
            type="text"
            placeholder="06:00"
            class="rounded-md border border-neutral-300 px-3 py-2"
          >
        </label>
        <div class="grid gap-1 text-sm sm:col-span-2">
          <span class="text-neutral-500">Location / site <span class="text-neutral-400">(optional)</span></span>
          <select
            v-model="locationChoice"
            class="rounded-md border border-neutral-300 px-3 py-2"
          >
            <option value="">
              — Select a site —
            </option>
            <option
              v-for="loc in locations"
              :key="loc.id"
              :value="siteLabel(loc)"
            >
              {{ siteLabel(loc) }}
            </option>
            <option :value="MANUAL">
              Other / type manually…
            </option>
          </select>
          <input
            v-if="locationChoice === MANUAL"
            v-model="manualLocation"
            type="text"
            placeholder="e.g. Priestfield — Up Main, Loc 40–52"
            class="mt-1 rounded-md border border-neutral-300 px-3 py-2"
          >
          <div class="mt-1">
            <button
              type="button"
              class="text-xs text-brand-primary hover:underline"
              @click="showNewSite = !showNewSite"
            >
              {{ showNewSite ? "Cancel" : "+ Add a new site to the register" }}
            </button>
          </div>
          <div
            v-if="showNewSite"
            class="mt-1 grid gap-2 rounded-lg bg-neutral-50 p-3 sm:grid-cols-3"
          >
            <input
              v-model="newSite.ref"
              placeholder="Ref / pole no. *"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
            <input
              v-model="newSite.name"
              placeholder="Name"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
            <input
              v-model="newSite.structure_type"
              placeholder="Type (pole, cantilever…)"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 sm:col-span-3"
              :disabled="addingSite || !newSite.ref.trim()"
              @click="addSite"
            >
              {{ addingSite ? "Adding…" : "Add site" }}
            </button>
          </div>
        </div>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Client <span class="text-neutral-400">(optional)</span></span>
          <input
            v-model="form.client_name"
            type="text"
            class="rounded-md border border-neutral-300 px-3 py-2"
            @input="touched.client_name = true"
          >
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">PO number <span class="text-neutral-400">(optional)</span></span>
          <input
            v-model="form.contract_po_number"
            type="text"
            class="rounded-md border border-neutral-300 px-3 py-2"
          >
        </label>
      </div>
      <p
        v-if="createError"
        class="mt-3 text-sm text-semantic-danger"
      >
        {{ createError }}
      </p>
      <button
        type="button"
        class="mt-4 rounded-md bg-brand-primary px-4 py-2 font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
        :disabled="creating || !projectName || !form.diary_date"
        @click="create"
      >
        {{ creating ? "Creating…" : "Create site diary" }}
      </button>
    </section>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="bg-semantic-danger/10 text-semantic-danger rounded-md p-4"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data && data.length === 0"
      class="text-neutral-500"
    >
      No site diaries have been created yet.
    </div>
    <div
      v-else-if="data"
      class="rounded-xl bg-neutral-0 shadow overflow-hidden"
    >
      <table class="w-full text-sm">
        <thead class="bg-neutral-50 text-neutral-500 text-left">
          <tr>
            <th class="px-4 py-2 font-medium">
              Date
            </th>
            <th class="px-4 py-2 font-medium">
              Project
            </th>
            <th class="px-4 py-2 font-medium">
              Rail week
            </th>
            <th class="px-4 py-2 font-medium">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="d in data"
            :key="d.id"
            class="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
            @click="open(d.id)"
          >
            <td class="px-4 py-2">
              {{ d.diary_date }}
            </td>
            <td class="px-4 py-2">
              {{ d.project_name }}
              <span
                v-if="d.project_code"
                class="text-neutral-400"
              >
                · {{ d.project_code }}
              </span>
            </td>
            <td class="px-4 py-2">
              {{ d.rail_week ?? "—" }}
            </td>
            <td class="px-4 py-2">
              <span
                class="rounded-full px-2 py-0.5 text-xs"
                :class="
                  d.status === 'submitted'
                    ? 'bg-semantic-success/10 text-semantic-success'
                    : 'bg-neutral-100 text-neutral-500'
                "
              >
                {{ d.status }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </PageLayout>
</template>
